import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { productsAPI, questionsAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useProposal } from '../contexts/ProposalContext';
import { formatCurrency, getStockStatusColor, formatDateTime } from '../utils/formatters';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { ChevronLeft, Edit, Trash2, MessageSquare, Send, Share2, FileText, Check } from 'lucide-react';
import { toast } from 'sonner';
import { shareProductOnWhatsApp } from '../utils/whatsappShare';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

export const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { addToProposal, isInProposal } = useProposal();
  const [product, setProduct] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const inProposal = product ? isInProposal(product.id) : false;

  useEffect(() => {
    loadProduct();
    loadQuestions();
  }, [id]);

  const loadProduct = async () => {
    try {
      const response = await productsAPI.getOne(id);
      setProduct(response.data);
    } catch (error) {
      toast.error('Failed to load product');
      navigate('/catalogue');
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await questionsAPI.getByProduct(id);
      setQuestions(response.data);
    } catch (error) {
      console.error('Failed to load questions:', error);
    }
  };

  const handleAskQuestion = async () => {
    if (!newQuestion.trim()) {
      toast.error('Please enter a question');
      return;
    }

    try {
      await questionsAPI.create({ product_id: id, question: newQuestion });
      toast.success('Question submitted successfully');
      setNewQuestion('');
      loadQuestions();
    } catch (error) {
      toast.error('Failed to submit question');
    }
  };

  const handleAnswerQuestion = async (questionId) => {
    if (!newAnswer[questionId]?.trim()) {
      toast.error('Please enter an answer');
      return;
    }

    try {
      await questionsAPI.answer(questionId, newAnswer[questionId]);
      toast.success('Answer submitted successfully');
      setNewAnswer({ ...newAnswer, [questionId]: '' });
      loadQuestions();
    } catch (error) {
      toast.error('Failed to submit answer');
    }
  };

  const handleDelete = async () => {
    try {
      await productsAPI.delete(id);
      toast.success('Product deleted successfully');
      navigate('/catalogue');
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleAddToProposal = () => {
    const added = addToProposal(product);
    if (added) {
      toast.success('Added to proposal');
    } else {
      toast.info('Already in proposal');
    }
  };

  const handleWhatsAppShare = () => {
    const appUrl = window.location.origin;
    shareProductOnWhatsApp(product, appUrl);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-consortium-green"></div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/catalogue')}
          className="mb-6 gap-2"
          data-testid="back-to-catalogue"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Catalogue
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
              <div className="aspect-[4/3] bg-white flex items-center justify-center">
                {product.images && product.images[selectedImage] ? (
                  <img
                    src={product.images[selectedImage].data}
                    alt={product.name}
                    className="!w-full !h-full object-contain"
                    data-testid="product-main-image"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <svg className="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
            {product.images && product.images.length > 1 && (
              <div className="grid grid-cols-5 gap-2">
                {product.images.map((img, idx) => (
                  <div
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`aspect-square rounded-lg overflow-hidden cursor-pointer border-2 bg-white flex items-center justify-center ${
                      selectedImage === idx ? 'border-consortium-green' : 'border-slate-200'
                    }`}
                    data-testid={`product-thumbnail-${idx}`}
                  >
                    <img 
                      src={img.data} 
                      alt={`${product.name} ${idx + 1}`} 
                      className="!w-full !h-full object-contain"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h1 className="font-barlow text-4xl font-bold text-slate-900 mb-3" data-testid="product-name">
                  {product.name}
                </h1>
                <div className="flex items-center gap-3 mb-4">
                  <Badge className="text-sm">{product.category}</Badge>
                  <Badge className={`${getStockStatusColor(product.stock_status)}`}>
                    {product.stock_status}
                  </Badge>
                </div>
              </div>
              {isAdmin() && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate(`/products/edit/${product.id}`)}
                    data-testid="edit-product-btn"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="icon" className="text-red-600" data-testid="delete-product-btn">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Product</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this product? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>

            <div className="bg-consortium-green-light border border-consortium-green/20 rounded-xl p-6 mb-6">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-consortium-green" data-testid="product-price">
                  {formatCurrency(product.recommended_selling_price)}
                </span>
                <span className="text-slate-600">/ unit</span>
              </div>
              <p className="text-slate-700 mt-2">Minimum Order: {product.moq} units</p>
              {isAdmin() && product.cost_price && (
                <p className="text-sm text-slate-600 mt-2">
                  Cost Price: {formatCurrency(product.cost_price)} (Admin Only)
                </p>
              )}
            </div>

            <div className="flex gap-3 mb-6">
              <Button
                onClick={handleAddToProposal}
                variant={inProposal ? "secondary" : "default"}
                className={`flex-1 gap-2 ${!inProposal && 'bg-consortium-green hover:bg-consortium-green-hover'}`}
                data-testid="add-to-proposal-detail-btn"
              >
                {inProposal ? (
                  <>
                    <Check className="w-4 h-4" />
                    In Proposal
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    Add to Proposal
                  </>
                )}
              </Button>
              <Button
                onClick={handleWhatsAppShare}
                variant="outline"
                className="gap-2"
                data-testid="whatsapp-share-btn"
              >
                <Share2 className="w-4 h-4" />
                Share
              </Button>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="font-barlow font-semibold text-lg text-slate-900 mb-2">Description</h3>
                <p className="text-slate-600 leading-relaxed" data-testid="product-description">{product.description}</p>
              </div>

              <Separator />

              <div>
                <h3 className="font-barlow font-semibold text-lg text-slate-900 mb-3">Product Details</h3>
                <dl className="space-y-2">
                  <div className="flex">
                    <dt className="w-40 text-slate-600 font-medium">Delivery Timeline:</dt>
                    <dd className="text-slate-900">{product.delivery_timeline}</dd>
                  </div>
                  <div className="flex">
                    <dt className="w-40 text-slate-600 font-medium">Colors/Variants:</dt>
                    <dd className="text-slate-900">{product.colors_variants}</dd>
                  </div>
                  {isAdmin() && product.sourcing_origin && (
                    <div className="flex">
                      <dt className="w-40 text-slate-600 font-medium">Sourcing Origin:</dt>
                      <dd className="text-slate-900">{product.sourcing_origin} (Admin Only)</dd>
                    </div>
                  )}
                </dl>
              </div>

              <Separator />

              <div>
                <h3 className="font-barlow font-semibold text-lg text-slate-900 mb-3">Branding Options</h3>
                <div className="grid grid-cols-2 gap-2">
                  {product.branding_options.logo_print && (
                    <Badge variant="secondary" className="justify-center">Logo Print</Badge>
                  )}
                  {product.branding_options.embroidery && (
                    <Badge variant="secondary" className="justify-center">Embroidery</Badge>
                  )}
                  {product.branding_options.engraving && (
                    <Badge variant="secondary" className="justify-center">Engraving</Badge>
                  )}
                  {product.branding_options.full_color_print && (
                    <Badge variant="secondary" className="justify-center">Full Color Print</Badge>
                  )}
                  {product.branding_options.debossing && (
                    <Badge variant="secondary" className="justify-center">Debossing</Badge>
                  )}
                  {product.branding_options.packaging_customization && (
                    <Badge variant="secondary" className="justify-center">Packaging Customization</Badge>
                  )}
                </div>
              </div>

              {product.tags && product.tags.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-barlow font-semibold text-lg text-slate-900 mb-3">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {product.tags.map((tag, idx) => (
                        <Badge key={idx} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <Separator className="my-12" />

        <div className="max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="w-6 h-6 text-consortium-green" />
            <h2 className="font-barlow text-2xl font-bold text-slate-900">Questions & Answers</h2>
          </div>

          {!isAdmin() && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <h3 className="font-semibold text-slate-900 mb-3">Ask a Question</h3>
              <Textarea
                placeholder="E.g., Can we do this in white with gold foil?"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                className="mb-3"
                data-testid="ask-question-input"
              />
              <Button
                onClick={handleAskQuestion}
                className="bg-consortium-green hover:bg-consortium-green-hover gap-2"
                data-testid="submit-question-btn"
              >
                <Send className="w-4 h-4" />
                Submit Question
              </Button>
            </div>
          )}

          <div className="space-y-4">
            {questions.length > 0 ? (
              questions.map((q) => (
                <div key={q.id} className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`question-${q.id}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                      <span className="font-semibold text-slate-700">{q.user_name.charAt(0)}</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{q.user_name}</p>
                      <p className="text-sm text-slate-500">{formatDateTime(q.created_at)}</p>
                    </div>
                    {q.status === 'pending' && (
                      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Pending</Badge>
                    )}
                  </div>
                  <p className="text-slate-700 mb-4">{q.question}</p>

                  {q.answer ? (
                    <div className="bg-consortium-green-light border border-consortium-green/20 rounded-lg p-4">
                      <p className="text-sm font-semibold text-consortium-green mb-1">Answered by {q.answered_by}</p>
                      <p className="text-slate-700">{q.answer}</p>
                    </div>
                  ) : isAdmin() ? (
                    <div>
                      <Textarea
                        placeholder="Enter your answer..."
                        value={newAnswer[q.id] || ''}
                        onChange={(e) => setNewAnswer({ ...newAnswer, [q.id]: e.target.value })}
                        className="mb-2"
                        data-testid={`answer-input-${q.id}`}
                      />
                      <Button
                        onClick={() => handleAnswerQuestion(q.id)}
                        size="sm"
                        className="bg-consortium-green hover:bg-consortium-green-hover"
                        data-testid={`submit-answer-btn-${q.id}`}
                      >
                        Submit Answer
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                <p className="text-slate-600">No questions yet. Be the first to ask!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
